import React, { ReactNode } from 'react';
import { cn } from '@Utils';

import { NotionBlock } from '@Models';
import { groupChildrenByType } from './helpers';
import RichText from './RichText';

interface NumberedListItemRendererProps {
  block: NotionBlock;
  level: number;
  defaultColor: string;
  defaultTextSize: string;
  renderChild: (block: NotionBlock, level: number) => ReactNode;
}

const NumberedListItemRenderer = ({
  block,
  level,
  defaultColor,
  defaultTextSize,
  renderChild,
}: NumberedListItemRendererProps) => {
  return (
    <li className={cn(defaultTextSize, 'leading-relaxed my-2')}>
      <RichText segments={block.content.rich_content || []} defaultColor={defaultColor} />
      {block.children && block.children.length > 0 && (
        <>
          {groupChildrenByType(block.children).map((group, idx) => {
            if (group.type === 'numbered_list_item') {
              // Same type - increment level and cycle every 3 levels (0, 1, 2, 0, 1, 2...)
              const nextLevel = (level + 1) % 3;
              return (
                <ol
                  key={group.type + String(idx)}
                  className={cn(
                    'my-2 pl-5',
                    nextLevel === 0 ? 'list-decimal' : nextLevel === 1 ? 'list-[lower-alpha]' : 'list-[lower-roman]',
                  )}>
                  {group.items.map((item) => renderChild(item, nextLevel))}
                </ol>
              );
            }
            if (group.type === 'bulleted_list_item') {
              // Different type - reset level to 0
              return (
                <ul key={group.type + String(idx)} className="list-disc my-2 pl-5">
                  {group.items.map((item) => renderChild(item, 0))}
                </ul>
              );
            }
            // Not a list item
            return group.items.map((item) => renderChild(item, level));
          })}
        </>
      )}
    </li>
  );
};

export default NumberedListItemRenderer;
